import React, { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import ImageManager from '../components/ImageManager';
import Statistics from '../components/Statistics';
import { PUERTO_RICO_CITIES } from '../utils/constants';
import { translateListingType } from '../utils/translations';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const { user } = useAuth();
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    bedrooms: '',
    bathrooms: '',
    area: '',
    yearBuilt: '',
    lotSizeM2: '',
    address: '',
    city: '',
    state: 'PR',
    zipCode: '',
    lat: '',
    lng: '',
    propertyType: 'house',
    status: 'available',
    listingType: 'for-sale',
    isReposeida: false,
    agent: 'Noemi Alicea',
    features: '',
    isDraft: false,
  });
  const [images, setImages] = useState(null);
  // imageFiles is used via functional state updates in handlePreviewImagesChange
  // eslint-disable-next-line no-unused-vars
  const [imageFiles, setImageFiles] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [imagesToDelete, setImagesToDelete] = useState([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState([]);
  const [formError, setFormError] = useState('');
  
  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCity, setFilterCity] = useState('');
  const [filterListingType, setFilterListingType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterMinPrice, setFilterMinPrice] = useState('');
  const [filterMaxPrice, setFilterMaxPrice] = useState('');
  const [filterDraftStatus, setFilterDraftStatus] = useState(''); // '' = all, 'draft' = drafts only, 'published' = published only
  
  // View state for switching between sections
  const [currentView, setCurrentView] = useState('properties'); // 'properties' or 'statistics'

  const fetchProperties = useCallback(async () => {
    try {
      // Fetch all properties without pagination for search/filter to work correctly
      // Include drafts for admin view
      const response = await axios.get('/api/properties?limit=1000&includeDrafts=true');
      
      // Handle both old and new API response formats
      const propertiesData = response.data.properties || response.data;
      
      setProperties(propertiesData);
    } catch (error) {
      console.error('Error fetching properties:', error);
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchProperties();
    }
  }, [user, fetchProperties]);

  // Apply search and filters whenever properties or filter values change
  useEffect(() => {
    let result = [...properties];

    // Apply search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(property => 
        (property.title && property.title.toLowerCase().includes(query)) ||
        (property.address && property.address.toLowerCase().includes(query)) ||
        (property.city && property.city.toLowerCase().includes(query))
      );
    }

    // Apply city filter
    if (filterCity) {
      result = result.filter(property => property.city === filterCity);
    }

    // Apply listing type filter
    if (filterListingType) {
      result = result.filter(property => property.listingType === filterListingType);
    }

    // Apply status filter
    if (filterStatus) {
      result = result.filter(property => property.status === filterStatus);
    }

    // Apply draft status filter
    if (filterDraftStatus === 'draft') {
      result = result.filter(property => property.isDraft === true);
    } else if (filterDraftStatus === 'published') {
      result = result.filter(property => !property.isDraft);
    }

    // Apply price range filter
    if (filterMinPrice) {
      const minPrice = parseFloat(filterMinPrice);
      if (!isNaN(minPrice)) {
        result = result.filter(property => property.price >= minPrice);
      }
    }
    if (filterMaxPrice) {
      const maxPrice = parseFloat(filterMaxPrice);
      if (!isNaN(maxPrice)) {
        result = result.filter(property => property.price <= maxPrice);
      }
    }

    // Show all filtered properties without pagination
    setFilteredProperties(result);
  }, [properties, searchQuery, filterCity, filterListingType, filterStatus, filterMinPrice, filterMaxPrice, filterDraftStatus]);

  const handleClearFilters = () => {
    setSearchQuery('');
    setFilterCity('');
    setFilterListingType('');
    setFilterStatus('');
    setFilterMinPrice('');
    setFilterMaxPrice('');
    setFilterDraftStatus('');
  };

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleFileChange = (e) => {
    const files = e.target.files;
    setImages(files);
    
    // Store files in an array for easier manipulation
    const filesArray = Array.from(files);
    setImageFiles(filesArray);
    
    // Generate preview URLs for the selected files in order
    if (files && files.length > 0) {
      const previewPromises = filesArray.map(file => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = () => reject(new Error('Failed to read file'));
          reader.readAsDataURL(file);
        });
      });
      
      Promise.all(previewPromises)
        .then(urls => {
          setImagePreviewUrls(urls);
        })
        .catch(error => {
          console.error('Error reading files:', error);
          setImagePreviewUrls([]);
        });
    } else {
      setImagePreviewUrls([]);
      setImageFiles([]);
    }
  };

  const handlePreviewImagesChange = useCallback((reorderedPreviews) => {
    setImagePreviewUrls(currentPreviews => {
      setImageFiles(currentFiles => {
        // Create a map of preview URL to file index for reliable matching
        const previewToFileMap = new Map();
        currentPreviews.forEach((url, index) => {
          previewToFileMap.set(url, index);
        });
        
        // Reorder/filter files to match the new preview order
        const reorderedFiles = reorderedPreviews
          .map(previewUrl => {
            const fileIndex = previewToFileMap.get(previewUrl);
            return fileIndex !== undefined ? currentFiles[fileIndex] : null;
          })
          .filter(Boolean);
        
        // Update the FileList for form submission
        // Use try-catch for DataTransfer compatibility
        try {
          const dataTransfer = new DataTransfer();
          reorderedFiles.forEach(file => dataTransfer.items.add(file));
          setImages(dataTransfer.files);
        } catch (error) {
          // Fallback: just keep the current images state as-is
          // The form submission already handles arrays via Array.from()
          console.warn('DataTransfer API not supported, using file array directly');
          setImages(reorderedFiles.length > 0 ? reorderedFiles : null);
        }
        
        return reorderedFiles;
      });
      
      return reorderedPreviews;
    });
  }, []);

  const handleImagesChange = async (reorderedImages, deletedImages = []) => {
    setExistingImages(reorderedImages);
    
    if (deletedImages.length > 0) {
      setImagesToDelete([...imagesToDelete, ...deletedImages]);
    }
    
    // If editing a property, update images on the server
    if (editingProperty) {
      try {
        await axios.patch(`/api/properties/${editingProperty.id}/images`, {
          images: reorderedImages,
          imagesToDelete: deletedImages
        });
      } catch (error) {
        console.error('Error updating images:', error);
        alert('Error updating images: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  const handleSubmit = async (e, saveAsDraft = false) => {
    e.preventDefault();
    setFormError('');

    // Client-side validation with specific error messages
    if (!formData.title?.trim()) {
      setFormError('Title is required');
      return;
    }
    if (!formData.description?.trim()) {
      setFormError('Description is required');
      return;
    }
    if (!formData.price || isNaN(Number(formData.price)) || Number(formData.price) <= 0) {
      setFormError('Valid price is required');
      return;
    }
    if (formData.bedrooms === '' || isNaN(Number(formData.bedrooms)) || Number(formData.bedrooms) < 0) {
      setFormError('Valid number of bedrooms is required (0 or more)');
      return;
    }
    if (formData.bathrooms === '' || isNaN(Number(formData.bathrooms)) || Number(formData.bathrooms) < 0) {
      setFormError('Valid number of bathrooms is required (0 or more)');
      return;
    }
    if (formData.area && (isNaN(Number(formData.area)) || Number(formData.area) < 0)) {
      setFormError('Area must be a valid number');
      return;
    }
    if (formData.yearBuilt && (isNaN(Number(formData.yearBuilt)) || Number(formData.yearBuilt) < 1800 || Number(formData.yearBuilt) > new Date().getFullYear() + 5)) {
      setFormError('Year built must be between 1800 and ' + (new Date().getFullYear() + 5));
      return;
    }
    if (formData.lotSizeM2 && (isNaN(Number(formData.lotSizeM2)) || Number(formData.lotSizeM2) < 0)) {
      setFormError('Lot size must be a valid number');
      return;
    }
    if (!formData.address?.trim()) {
      setFormError('Address is required');
      return;
    }
    if (!formData.city?.trim()) {
      setFormError('City is required');
      return;
    }
    if (!formData.state?.trim()) {
      setFormError('State is required');
      return;
    }
    if (!formData.zipCode?.trim()) {
      setFormError('Zip code is required');
      return;
    }
    if (formData.lat && String(formData.lat).trim() && isNaN(Number(formData.lat))) {
      setFormError('Latitude must be a valid number');
      return;
    }
    if (formData.lng && String(formData.lng).trim() && isNaN(Number(formData.lng))) {
      setFormError('Longitude must be a valid number');
      return;
    }

    const data = new FormData();
    Object.keys(formData).forEach(key => {
      if (key === 'features') {
        data.append(key, JSON.stringify(formData[key].split(',').map(f => f.trim()).filter(f => f)));
      } else if (key === 'isDraft') {
        // Override with saveAsDraft parameter
        data.append(key, saveAsDraft);
      } else {
        data.append(key, formData[key]);
      }
    });

    if (images) {
      Array.from(images).forEach(file => {
        data.append('images', file);
      });
    }

    try {
      if (editingProperty) {
        await axios.put(`/api/properties/${editingProperty.id}`, data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        await axios.post('/api/properties', data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      setShowForm(false);
      setEditingProperty(null);
      resetForm();
      fetchProperties();
    } catch (error) {
      console.error('Error saving property:', error);
      let errorMessage = 'An unknown error occurred';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setFormError(errorMessage);
      // Scroll to error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      price: '',
      bedrooms: '',
      bathrooms: '',
      area: '',
      yearBuilt: '',
      lotSizeM2: '',
      address: '',
      city: '',
      state: 'PR',
      zipCode: '',
      lat: '',
      lng: '',
      propertyType: 'house',
      status: 'available',
      listingType: 'for-sale',
      isReposeida: false,
      agent: 'Noemi Alicea',
      features: '',
      isDraft: false,
    });
    setImages(null);
    setImageFiles([]);
    setExistingImages([]);
    setImagesToDelete([]);
    setImagePreviewUrls([]);
    setFormError('');
  };

  const handleEdit = (property) => {
    setEditingProperty(property);
    setFormData({
      title: property.title,
      description: property.description,
      price: property.price,
      bedrooms: property.bedrooms,
      bathrooms: property.bathrooms,
      area: property.area || '',
      yearBuilt: property.yearBuilt || '',
      lotSizeM2: property.lotSizeM2 || '',
      address: property.address,
      city: property.city,
      state: property.state,
      zipCode: property.zipCode,
      lat: property.coordinates.lat,
      lng: property.coordinates.lng,
      propertyType: property.propertyType,
      status: property.status,
      listingType: property.listingType || 'for-sale',
      isReposeida: property.isReposeida || false,
      agent: property.agent || 'Noemi Alicea',
      features: property.features.join(', '),
      isDraft: property.isDraft || false,
    });
    setExistingImages(property.images || []);
    setImagesToDelete([]);
    setImageFiles([]);
    setImagePreviewUrls([]);
    setFormError('');
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this property?')) {
      try {
        await axios.delete(`/api/properties/${id}`);
        fetchProperties();
      } catch (error) {
        console.error('Error deleting property:', error);
        alert('Error deleting property');
      }
    }
  };

  const handleToggleFeatured = async (id, currentFeaturedStatus) => {
    try {
      await axios.patch(`/api/properties/${id}/featured`, {
        isFeatured: !currentFeaturedStatus
      });
      fetchProperties();
    } catch (error) {
      console.error('Error toggling featured status:', error);
      alert(error.response?.data?.message || 'Error toggling featured status');
    }
  };

  if (!user) {
    return <Navigate to="/login" />;
  }

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <h1>Admin Dashboard</h1>
        <div className="dashboard-actions">
          {currentView === 'properties' && (
            <button
              className="add-button"
              onClick={() => {
                setShowForm(!showForm);
                setEditingProperty(null);
                resetForm();
              }}
            >
              {showForm ? 'Cancel' : 'Add New Property'}
            </button>
          )}
        </div>
      </div>

      <div className="dashboard-navigation">
        <button
          className={`nav-button ${currentView === 'properties' ? 'active' : ''}`}
          onClick={() => setCurrentView('properties')}
        >
          📋 Propiedades
        </button>
        <button
          className={`nav-button ${currentView === 'statistics' ? 'active' : ''}`}
          onClick={() => {
            setCurrentView('statistics');
            setShowForm(false); // Close form when switching to statistics
          }}
        >
          📊 Estadísticas
        </button>
      </div>

      {currentView === 'statistics' && <Statistics />}

      {currentView === 'properties' && (
        <>

      {showForm && (
        <div className="property-form-container">
          <h2>{editingProperty ? 'Edit Property' : 'Add New Property'}</h2>
          {formError && (
            <div className="form-error-message">
              {formError}
            </div>
          )}
          <form onSubmit={handleSubmit} className="property-form">
            <div className="form-row">
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Listing Type *</label>
                <select
                  name="listingType"
                  value={formData.listingType}
                  onChange={handleChange}
                  required
                >
                  <option value="for-sale">For Sale</option>
                  <option value="for-rent">For Rent</option>
                  <option value="sold">Sold</option>
                  <option value="archived">Archivada</option>
                </select>
              </div>
              <div className="form-group">
                <label>Agent *</label>
                <select
                  name="agent"
                  value={formData.agent}
                  onChange={handleChange}
                  required
                >
                  <option value="Noemi Alicea">Noemi Alicea</option>
                  <option value="Lina Lucia">Lina Lucia</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{formData.listingType === 'for-rent' ? 'Monthly Rent *' : 'Price *'}</label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  step="1"
                  required
                />
              </div>
              {(formData.listingType === 'for-sale' || formData.listingType === 'sold') && (
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      name="isReposeida"
                      checked={formData.isReposeida}
                      onChange={handleChange}
                      style={{ width: 'auto', marginRight: '8px' }}
                    />
                    Reposeida
                  </label>
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Description *</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="4"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Bedrooms *</label>
                <input
                  type="number"
                  name="bedrooms"
                  value={formData.bedrooms}
                  onChange={handleChange}
                  min="0"
                  step="1"
                  required
                />
              </div>
              <div className="form-group">
                <label>Bathrooms *</label>
                <input
                  type="number"
                  name="bathrooms"
                  value={formData.bathrooms}
                  onChange={handleChange}
                  min="0"
                  step="0.5"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Año de Construcción</label>
                <input
                  type="number"
                  name="yearBuilt"
                  value={formData.yearBuilt}
                  onChange={handleChange}
                  placeholder="2020"
                  min="1800"
                  max={new Date().getFullYear() + 5}
                />
              </div>
              <div className="form-group">
                <label>Metros Cuadrados (Lot Size)</label>
                <input
                  type="number"
                  step="0.01"
                  name="lotSizeM2"
                  value={formData.lotSizeM2}
                  onChange={handleChange}
                  placeholder="500.50"
                  min="0"
                />
              </div>
              <div className="form-group">
                <label>Pies Cuadrados (Living Area)</label>
                <input
                  type="number"
                  name="area"
                  value={formData.area}
                  onChange={handleChange}
                  placeholder="1500"
                  min="0"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Address *</label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>City *</label>
                <select
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select a city</option>
                  {PUERTO_RICO_CITIES.map((city) => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>State *</label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  required
                  readOnly
                />
              </div>
              <div className="form-group">
                <label>Zip Code *</label>
                <input
                  type="text"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Latitude</label>
                <input
                  type="text"
                  name="lat"
                  value={formData.lat}
                  onChange={handleChange}
                  placeholder="18.0000"
                />
              </div>
              <div className="form-group">
                <label>Longitude</label>
                <input
                  type="text"
                  name="lng"
                  value={formData.lng}
                  onChange={handleChange}
                  placeholder="-66.0000"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Property Type *</label>
                <select
                  name="propertyType"
                  value={formData.propertyType}
                  onChange={handleChange}
                  required
                >
                  <option value="house">House</option>
                  <option value="apartment">Apartment</option>
                  <option value="condo">Condo</option>
                  <option value="walkup">Walk Up</option>
                  <option value="multifamiliar">Multifamiliar</option>
                  <option value="finca">Finca</option>
                  <option value="duplex">Duplex</option>
                  <option value="land">Land</option>
                  <option value="commercial">Commercial</option>
                </select>
              </div>
              <div className="form-group">
                <label>Status *</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  required
                >
                  <option value="available">Disponible</option>
                  <option value="pending">Opcionada</option>
                  <option value="sold">Vendida</option>
                  <option value="rented">Rentada</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Features (comma-separated)</label>
              <input
                type="text"
                name="features"
                value={formData.features}
                onChange={handleChange}
                placeholder="Pool, Garage, Garden, etc."
              />
            </div>

            {editingProperty && existingImages.length > 0 && (
              <div className="form-group">
                <label>Current Images</label>
                <ImageManager
                  existingImages={existingImages}
                  onImagesChange={handleImagesChange}
                  propertyId={editingProperty.id}
                />
              </div>
            )}

            {!editingProperty && imagePreviewUrls.length > 0 && (
              <div className="form-group">
                <label>Image Previews</label>
                <ImageManager
                  existingImages={imagePreviewUrls}
                  onImagesChange={handlePreviewImagesChange}
                />
              </div>
            )}

            <div className="form-group">
              <label>{editingProperty ? 'Add More Images' : 'Images'}</label>
              <input
                type="file"
                accept="image/*,.heic,.heif"
                multiple
                onChange={handleFileChange}
              />
            </div>

            <div className="form-actions">
              <button 
                type="button" 
                onClick={(e) => handleSubmit(e, true)} 
                className="draft-button"
              >
                {editingProperty ? (formData.isDraft ? 'Update Draft' : 'Save as Draft') : 'Save as Draft'}
              </button>
              <button type="submit" className="submit-button">
                {editingProperty ? (formData.isDraft ? 'Publish Property' : 'Update Property') : 'Publish Property'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="search-filter-container">
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search by title, address, or city..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="filters-section">
          <div className="filter-group">
            <label>City</label>
            <select
              value={filterCity}
              onChange={(e) => setFilterCity(e.target.value)}
              className="filter-select"
            >
              <option value="">All Cities</option>
              {PUERTO_RICO_CITIES.map((city) => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Listing Type</label>
            <select
              value={filterListingType}
              onChange={(e) => setFilterListingType(e.target.value)}
              className="filter-select"
            >
              <option value="">All Types</option>
              <option value="for-sale">For Sale</option>
              <option value="for-rent">For Rent</option>
              <option value="sold">Sold</option>
              <option value="archived">Archivada</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="filter-select"
            >
              <option value="">All Statuses</option>
              <option value="available">Disponible</option>
              <option value="pending">Opcionada</option>
              <option value="sold">Vendida</option>
              <option value="rented">Rentada</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Min Price</label>
            <input
              type="number"
              placeholder="$0"
              value={filterMinPrice}
              onChange={(e) => setFilterMinPrice(e.target.value)}
              className="filter-input"
            />
          </div>

          <div className="filter-group">
            <label>Max Price</label>
            <input
              type="number"
              placeholder="No limit"
              value={filterMaxPrice}
              onChange={(e) => setFilterMaxPrice(e.target.value)}
              className="filter-input"
            />
          </div>

          <div className="filter-group">
            <label>Draft Status</label>
            <select
              value={filterDraftStatus}
              onChange={(e) => setFilterDraftStatus(e.target.value)}
              className="filter-select"
            >
              <option value="">All Properties</option>
              <option value="draft">Drafts Only</option>
              <option value="published">Published Only</option>
            </select>
          </div>

          <div className="filter-group">
            <button
              type="button"
              onClick={handleClearFilters}
              className="clear-filters-btn"
            >
              Clear Filters
            </button>
          </div>
        </div>

        <div className="results-count">
          Showing {filteredProperties.length} of {properties.length} properties
        </div>
      </div>

      <div className="properties-list">
        <h2>All Properties</h2>
        {filteredProperties.length === 0 ? (
          <p>{properties.length === 0 ? 'No properties yet. Add your first property!' : 'No properties match your search criteria.'}</p>
        ) : (
          <>
            <div className="properties-table">
              <table>
                <thead>
                  <tr>
                    <th>Featured</th>
                    <th>Title</th>
                    <th>Type</th>
                    <th>Price</th>
                    <th>Location</th>
                    <th>Beds/Baths</th>
                    <th>Status</th>
                    <th>Views</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProperties.map((property) => (
                    <tr key={property.id}>
                      <td>
                        <button
                          className={`featured-btn ${property.isFeatured ? 'featured' : ''}`}
                          onClick={() => handleToggleFeatured(property.id, property.isFeatured)}
                          title={property.isFeatured ? 'Remove from featured' : 'Add to featured'}
                        >
                          {property.isFeatured ? '★' : '☆'}
                        </button>
                      </td>
                      <td>
                        {property.title}
                        {property.isReposeida && <span className="reposeida-label"> (Reposeida)</span>}
                        {property.isDraft && <span className="draft-label"> (Borrador)</span>}
                        {property.listingType === 'archived' && <span className="archived-label"> (Archivada)</span>}
                      </td>
                      <td>{translateListingType(property.listingType || 'for-sale')}</td>
                      <td>
                        ${property.price.toLocaleString()}
                        {property.listingType === 'for-rent' && '/mo'}
                      </td>
                      <td>{property.city}, {property.state}</td>
                      <td>{property.bedrooms} / {property.bathrooms}</td>
                      <td>
                        <span className={`status-badge ${property.status}`}>
                          {property.status}
                        </span>
                      </td>
                      <td>
                        <span className="view-count">{property.viewCount || 0}</span>
                      </td>
                      <td className="actions">
                        <button
                          className="edit-btn"
                          onClick={() => handleEdit(property)}
                        >
                          Edit
                        </button>
                        <button
                          className="delete-btn"
                          onClick={() => handleDelete(property.id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
      </>
      )}
    </div>
  );
};

export default AdminDashboard;
